import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { getMyProfile } from "../../../core/data/redux/employeesSlice";
import type { AppDispatch, RootState } from "../../../core/data/redux/store";
import {
  ATTACHMENT_MAX_BYTES,
  DEDUCTION_NOTE,
  MANDATORY_ATTACHMENT_NOTES,
  PHOTO_MAX_BYTES,
  PHOTO_MIN_BYTES,
  REASONS_WITHOUT_UPLOAD,
  SUCCESS_SUBMIT_MESSAGE,
  apiMessage,
  createSv4Card,
  getAttachmentMimeAndKind,
  getEmployeeDisplayName,
  getLoggedInEmpId,
  getProfileDetails,
  getSv4Reasons,
  isAllowedAttachment,
  uploadSv4File,
} from "./sv4Card.service";

interface ReasonOption {
  Value: string;
  Ddtext: string;
}

interface UploadedDoc {
  uid: string;
  fileName: string;
  serverKey: string;
  mime?: string;
}

interface FormErrors {
  placeofposting?: string;
  contact?: string;
  reason?: string;
  userRemarks?: string;
  attachment?: string;
  photo?: string;
}

const ReadOnlyField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="col-md-6">
    <label className="form-label fw-semibold small">{label}</label>
    <input type="text" className="form-control bg-light text-muted" value={value} readOnly tabIndex={-1} />
  </div>
);

const Sv4CardCreate: React.FC = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const employeesState = useSelector((state: RootState) => state.employeesState);

  const reduxDetails = employeesState?.data?.data?.details;
  const profile = getProfileDetails(reduxDetails);
  const empId = String(profile?.empId || getLoggedInEmpId() || "");
  const employeeName = getEmployeeDisplayName(profile);
  const designation = profile?.sap_department_designation_wings?.designation_name || "";
  const department = profile?.sap_department_designation_wings?.department_name || "";
  const wing = profile?.sap_department_designation_wings?.wing_name || "";
  const employeeGroup = profile?.blood_group || "";

  const [placeofposting, setPlaceofposting] = useState("");
  const [contact, setContact] = useState("");
  const [existingcardnumber, setExistingcardnumber] = useState("");
  const [selectedReason, setSelectedReason] = useState<ReasonOption | null>(null);
  const [userRemarks, setUserRemarks] = useState("");

  const [reasonOptions, setReasonOptions] = useState<ReasonOption[]>([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);

  const [attachmentDocs, setAttachmentDocs] = useState<UploadedDoc[]>([]);
  const [photoDocs, setPhotoDocs] = useState<UploadedDoc[]>([]);
  const [attachUploading, setAttachUploading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    dispatch(getMyProfile({}));
  }, [dispatch]);

  useEffect(() => {
    const loadReasons = async () => {
      setReasonsLoading(true);
      try {
        const optionsList = await getSv4Reasons();
        setReasonOptions(optionsList);
      } catch {
        toast.error("Failed to load reason options");
        setReasonOptions([]);
      } finally {
        setReasonsLoading(false);
      }
    };
    loadReasons();
  }, []);

  const handleReasonChange = (val: string) => {
    const found = reasonOptions.find((r) => r.Value === val) || null;
    setSelectedReason(found);
    setAttachmentDocs([]);
    setPhotoDocs([]);
    setErrors((prev) => ({ ...prev, reason: "", attachment: "", photo: "", userRemarks: "" }));
    if (found?.Value !== "V7") setUserRemarks("");
  };

  const handleAttachmentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;

    setAttachUploading(true);
    const uploaded: UploadedDoc[] = [];
    try {
      for (const file of files) {
        if (!isAllowedAttachment(file)) {
          toast.error("Only PDF, JPG or JPEG files are allowed for the attachment.");
          continue;
        }
        if (file.size > ATTACHMENT_MAX_BYTES) {
          toast.error(`${file.name}: size must not exceed 2 MB. Please choose a smaller file.`);
          continue;
        }
        const kind = getAttachmentMimeAndKind(file);
        if (!kind) continue;
        try {
          const key = await uploadSv4File(file, empId, kind.mime);
          uploaded.push({
            uid: crypto.randomUUID(),
            fileName: file.name,
            serverKey: key,
            mime: kind.mime,
          });
        } catch (error: any) {
          toast.error(error?.message || `Failed to upload ${file.name}`);
        }
      }
      if (uploaded.length > 0) {
        setAttachmentDocs((prev) => [...prev, ...uploaded]);
        setErrors((prev) => ({ ...prev, attachment: "" }));
        toast.success(
          uploaded.length > 1
            ? `${uploaded.length} files uploaded successfully`
            : "File uploaded successfully",
        );
      }
    } finally {
      setAttachUploading(false);
    }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (photoDocs.length >= 1) {
      toast.info("Only one photo is allowed. Delete the current photo to upload a different one.");
      return;
    }

    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (ext !== "jpg" && ext !== "jpeg") {
      toast.error("Only JPG/JPEG files are allowed for photo upload.");
      return;
    }
    if (!file.size) {
      toast.error("Could not read file size. Please try another image.");
      return;
    }
    if (file.size < PHOTO_MIN_BYTES) {
      toast.error(
        `Photo size is too small (${(file.size / 1024).toFixed(1)} KB). It must be between 20 KB and 50 KB.`,
      );
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      toast.error(
        `Photo size is too large (${(file.size / 1024).toFixed(1)} KB). It must be between 20 KB and 50 KB.`,
      );
      return;
    }

    setPhotoUploading(true);
    try {
      const key = await uploadSv4File(file, empId, "image/jpeg");
      setPhotoDocs([{ uid: crypto.randomUUID(), fileName: file.name, serverKey: key, mime: "image/jpeg" }]);
      setErrors((prev) => ({ ...prev, photo: "" }));
      toast.success("File uploaded successfully");
    } catch (error: any) {
      toast.error(error?.message || "Failed to upload photo");
    } finally {
      setPhotoUploading(false);
    }
  };

  const validate = (): boolean => {
    const errs: FormErrors = {};
    if (!empId) {
      toast.error("Employee profile not loaded yet. Please wait a moment.");
      return false;
    }
    if (!placeofposting.trim()) errs.placeofposting = "This field is required";
    if (!contact.trim()) errs.contact = "This field is required";
    if (!selectedReason) errs.reason = "This field is required";

    const reasonCode = selectedReason?.Value ?? "";
    const noUpload = REASONS_WITHOUT_UPLOAD.has(reasonCode);

    if (reasonCode === "V7" && !userRemarks.trim()) {
      errs.userRemarks = "This field is required";
    }
    if (!noUpload && selectedReason && attachmentDocs.length === 0) {
      errs.attachment = "Please upload the required attachment";
    }
    if (!noUpload && selectedReason && photoDocs.length === 0) {
      errs.photo = "Please upload the required photo";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const reasonCode = selectedReason!.Value;
      const noUpload = REASONS_WITHOUT_UPLOAD.has(reasonCode);

      const payload: Record<string, any> = {
        Action: "C",
        ReasonToApply: reasonCode,
        EmpID: String(empId),
        ExistingCardNumber: existingcardnumber.trim(),
        PlaceOfPosting: placeofposting.trim(),
        ContactNumber: contact.trim(),
      };

      if (reasonCode === "V7") {
        payload.UserRemarks = userRemarks.trim();
      }

      if (!noUpload) {
        payload.ToDocuments = {
          results: attachmentDocs.map((d) => ({
            FileName: d.fileName,
            Content: d.serverKey ?? "",
          })),
        };
        payload.ToPicture = {
          results: photoDocs.map((d) => ({
            FileName: d.fileName,
            Content: d.serverKey ?? "",
          })),
        };
      }

      const res = await createSv4Card(payload);
      toast.success(apiMessage(res, "Request submitted successfully"));
      setShowSuccess(true);
    } catch (error: any) {
      toast.error(error?.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const noUploadNeeded = selectedReason ? REASONS_WITHOUT_UPLOAD.has(selectedReason.Value) : false;
  const remarksNeeded = selectedReason?.Value === "V7";

  if (showSuccess) {
    return (
      <div className="page-wrapper">
        <div className="content d-flex flex-column align-items-center justify-content-center py-5">
          <div className="text-center" style={{ maxWidth: 520 }}>
            <div
              className="rounded-circle bg-success d-flex align-items-center justify-content-center mx-auto mb-4"
              style={{ width: 80, height: 80 }}
            >
              <i className="ti ti-check text-white" style={{ fontSize: 40 }} />
            </div>
            <h4 className="fw-bold mb-2">Request Submitted!</h4>
            <p className="text-muted mb-4">{SUCCESS_SUBMIT_MESSAGE}</p>
            <button className="btn btn-primary" onClick={() => navigate(routes.sv4Card.list)}>
              Back to List
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">SV4 Card Issue/Re-issue</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.sv4Card.list}>SV4 Card</Link>
                </li>
                <li className="breadcrumb-item active">Create Request</li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white border-bottom">
            <h5 className="mb-0 fw-bold">
              <i className="ti ti-id-badge-2 me-2 text-danger" />
              Application Form SV4 Card (Issue/Reissue)
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <ReadOnlyField label="Employee Name" value={employeeName} />
              <ReadOnlyField label="Personal Number" value={empId} />
              <ReadOnlyField label="Designation" value={designation} />
              <ReadOnlyField label="Department" value={department} />
              <ReadOnlyField label="Wing / Project / O&M" value={wing} />
              <ReadOnlyField label="Employee Group" value={employeeGroup} />

              <div className="col-md-6">
                <label className="form-label fw-semibold small">
                  Place of Posting <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.placeofposting ? "is-invalid" : ""}`}
                  value={placeofposting}
                  onChange={(e) => {
                    setPlaceofposting(e.target.value);
                    setErrors((prev) => ({ ...prev, placeofposting: "" }));
                  }}
                  placeholder="Enter place of posting"
                />
                {errors.placeofposting && <div className="invalid-feedback">{errors.placeofposting}</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold small">
                  Contact/Extension No. <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.contact ? "is-invalid" : ""}`}
                  value={contact}
                  onChange={(e) => {
                    setContact(e.target.value);
                    setErrors((prev) => ({ ...prev, contact: "" }));
                  }}
                  placeholder="Enter contact/extension no."
                />
                {errors.contact && <div className="invalid-feedback">{errors.contact}</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold small">Existing card number</label>
                <input
                  type="text"
                  className="form-control"
                  value={existingcardnumber}
                  onChange={(e) => setExistingcardnumber(e.target.value)}
                  placeholder="Enter existing card number"
                />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold small">
                  Application applied for the reason <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${errors.reason ? "is-invalid" : ""}`}
                  value={selectedReason?.Value || ""}
                  onChange={(e) => handleReasonChange(e.target.value)}
                  disabled={reasonsLoading}
                >
                  <option value="">{reasonsLoading ? "Loading..." : "Select Purpose of ID"}</option>
                  {reasonOptions.map((r) => (
                    <option key={r.Value} value={r.Value}>
                      {r.Ddtext}
                    </option>
                  ))}
                </select>
                {errors.reason && <div className="invalid-feedback">{errors.reason}</div>}
              </div>

              {remarksNeeded && (
                <div className="col-12">
                  <label className="form-label fw-semibold small">
                    Enter Remarks <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${errors.userRemarks ? "is-invalid" : ""}`}
                    rows={3}
                    value={userRemarks}
                    onChange={(e) => {
                      setUserRemarks(e.target.value);
                      setErrors((prev) => ({ ...prev, userRemarks: "" }));
                    }}
                    placeholder="Enter Remarks"
                  />
                  {errors.userRemarks && <div className="invalid-feedback">{errors.userRemarks}</div>}
                </div>
              )}

              <div className="col-12">
                <div className="border rounded-3 p-4">
                  <h6 className="fw-bold mb-3">Mandatory Attachments</h6>
                  {MANDATORY_ATTACHMENT_NOTES.map((note) => (
                    <p key={note} className="small mb-2 text-dark">
                      {note}
                    </p>
                  ))}
                </div>
              </div>

              {selectedReason && !noUploadNeeded && (
                <>
                  <div className="col-12">
                    <label className="form-label fw-semibold small">
                      Attachment <span className="text-danger">*</span>
                    </label>
                    <div className="text-muted small mb-1">PDF/JPG/JPEG files are allowed</div>
                    <div
                      className={`border rounded-3 p-3 d-flex align-items-center justify-content-between ${
                        errors.attachment ? "border-danger" : ""
                      }`}
                    >
                      <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                        {attachUploading ? (
                          <span className="spinner-border spinner-border-sm text-danger" />
                        ) : (
                          <i className="ti ti-paperclip text-danger" style={{ fontSize: 22 }} />
                        )}
                        <span className="small text-dark">{attachUploading ? "Uploading..." : "Choose File"}</span>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg"
                          className="d-none"
                          multiple
                          onChange={handleAttachmentChange}
                          disabled={attachUploading}
                        />
                      </label>
                      <label className="btn btn-danger btn-sm d-flex align-items-center gap-1 mb-0" style={{ cursor: "pointer" }}>
                        {attachUploading ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          <i className="ti ti-cloud-upload" />
                        )}
                        <span>Choose file to upload</span>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg"
                          className="d-none"
                          multiple
                          onChange={handleAttachmentChange}
                          disabled={attachUploading}
                        />
                      </label>
                    </div>
                    {errors.attachment && <div className="text-danger small mt-1">{errors.attachment}</div>}
                    {attachmentDocs.map((item) => (
                      <div
                        key={item.uid}
                        className="border rounded-3 p-3 d-flex align-items-center justify-content-between bg-light mt-2"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <i className="ti ti-file text-danger" style={{ fontSize: 22 }} />
                          <span className="small text-truncate" style={{ maxWidth: 320 }}>
                            {item.fileName}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setAttachmentDocs((prev) => prev.filter((d) => d.uid !== item.uid))}
                        >
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold small">
                      Upload Photo (20 to 50kb) <span className="text-danger">*</span>
                    </label>
                    <div className="text-muted small mb-1">
                      Only JPG/JPEG — file size must be between 20 KB and 50 KB.
                    </div>
                    <div
                      className={`border rounded-3 p-3 d-flex align-items-center justify-content-between ${
                        errors.photo ? "border-danger" : ""
                      }`}
                    >
                      <label
                        className={`d-flex align-items-center gap-2 mb-0 ${
                          photoDocs.length > 0 ? "opacity-50" : ""
                        }`}
                        style={{ cursor: photoDocs.length > 0 ? "not-allowed" : "pointer" }}
                      >
                        {photoUploading ? (
                          <span className="spinner-border spinner-border-sm text-danger" />
                        ) : (
                          <i className="ti ti-photo text-danger" style={{ fontSize: 22 }} />
                        )}
                        <span className="small text-dark">{photoUploading ? "Uploading..." : "Choose File"}</span>
                        <input
                          type="file"
                          accept=".jpg,.jpeg,image/jpeg"
                          className="d-none"
                          onChange={handlePhotoChange}
                          disabled={photoUploading || photoDocs.length > 0}
                        />
                      </label>
                      <label
                        className={`btn btn-danger btn-sm d-flex align-items-center gap-1 mb-0 ${
                          photoDocs.length > 0 ? "disabled" : ""
                        }`}
                        style={{ cursor: photoDocs.length > 0 ? "not-allowed" : "pointer" }}
                      >
                        {photoUploading ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          <i className="ti ti-cloud-upload" />
                        )}
                        <span>Choose file to upload</span>
                        <input
                          type="file"
                          accept=".jpg,.jpeg,image/jpeg"
                          className="d-none"
                          onChange={handlePhotoChange}
                          disabled={photoUploading || photoDocs.length > 0}
                        />
                      </label>
                    </div>
                    {errors.photo && <div className="text-danger small mt-1">{errors.photo}</div>}
                    {photoDocs.map((item) => (
                      <div
                        key={item.uid}
                        className="border rounded-3 p-3 d-flex align-items-center justify-content-between bg-light mt-2"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <i className="ti ti-photo text-success" style={{ fontSize: 22 }} />
                          <span className="small text-truncate" style={{ maxWidth: 320 }}>
                            {item.fileName}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setPhotoDocs([])}
                        >
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="col-12">
                <div className="border-start border-4 border-danger bg-danger-subtle px-3 py-2 rounded-end">
                  <span className="text-danger fw-semibold small">* {DEDUCTION_NOTE}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="card-footer bg-white border-top d-flex justify-content-between gap-3">
            <Link to={routes.sv4Card.list} className="btn btn-outline-secondary">
              <i className="ti ti-x me-1" />
              Cancel
            </Link>
            <button
              className="btn btn-danger px-5"
              onClick={handleSubmit}
              disabled={isSubmitting || attachUploading || photoUploading}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" />
                  Submitting...
                </>
              ) : (
                "SUBMIT"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sv4CardCreate;
